// ABOUTME: Custom hook for managing table sorting state and logic.
// ABOUTME: Supports ascending/descending sort with configurable default column.

import { useReducer, useMemo, useCallback } from 'react';

// Reducer defined outside hook to maintain stable reference
const sortReducer = (state, action) => {
  if (action.type === 'SORT') {
    const { key } = action;
    if (state.key === key) {
      // Same column: toggle direction
      return {
        key,
        direction: state.direction === 'asc' ? 'desc' : 'asc',
      };
    } else {
      // New column: always start with descending
      return {
        key,
        direction: 'desc',
      };
    }
  }
  return state;
};

/**
 * Hook for managing sortable table state.
 *
 * @param {Array} items - Array of items to sort
 * @param {Object} options - Configuration options
 * @param {string} options.defaultSortKey - Default column to sort by (default: 'urgency_score')
 * @param {string} options.defaultSortDirection - Default sort direction: 'asc' or 'desc' (default: 'desc')
 * @param {Array} options.allowedSortKeys - If provided, only these keys can be sorted (for grouped mode)
 * @returns {Object} - { sortedItems, sortKey, sortDirection, handleSort, isSortable }
 */
function useSortableTable(items, options = {}) {
  const {
    defaultSortKey = 'urgency_score',
    defaultSortDirection = 'desc',
    allowedSortKeys = null,
  } = options;

  const [sortState, dispatch] = useReducer(sortReducer, {
    key: defaultSortKey,
    direction: defaultSortDirection,
  });

  const isSortable = useCallback((key) => {
    if (!allowedSortKeys) return true;
    return allowedSortKeys.includes(key);
  }, [allowedSortKeys]);

  const handleSort = useCallback((key) => {
    if (!isSortable(key)) return;
    dispatch({ type: 'SORT', key });
  }, [isSortable]);

  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return items;

    const { key: sortKey, direction: sortDirection } = sortState;

    // Helper to get nested property value (e.g., "category.name")
    const getNestedValue = (obj, key) => {
      return key.split('.').reduce((o, k) => (o && o[k] !== undefined) ? o[k] : null, obj);
    };

    // Helper to determine if value should be treated as numeric
    const isNumeric = (val) => {
      if (typeof val === 'number') return true;
      if (typeof val === 'string') {
        // Only treat as numeric if the ENTIRE string is a valid number
        // This prevents "2024-01-30" from being parsed as 2024
        const trimmed = val.trim();
        if (trimmed === '') return false;
        const num = Number(trimmed);
        return !isNaN(num) && isFinite(num);
      }
      return false;
    };

    const sorted = [...items].sort((a, b) => {
      let aVal = getNestedValue(a, sortKey);
      let bVal = getNestedValue(b, sortKey);

      // Handle null/undefined values - put them at the end
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Determine comparison type based on the first non-null values
      const aIsNumeric = isNumeric(aVal);
      const bIsNumeric = isNumeric(bVal);

      // If both can be numeric, compare as numbers
      if (aIsNumeric && bIsNumeric) {
        const aNum = typeof aVal === 'number' ? aVal : parseFloat(aVal);
        const bNum = typeof bVal === 'number' ? bVal : parseFloat(bVal);
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Otherwise compare as strings
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [items, sortState]);

  return {
    sortedItems,
    sortKey: sortState.key,
    sortDirection: sortState.direction,
    handleSort,
    isSortable,
  };
}

export default useSortableTable;
